import { Temporal } from "@js-temporal/polyfill";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createChore, type Schedule } from "@repo/api/client";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { FieldErrors } from "@/components/form/field-errors";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DAY_OPTIONS = [
  { label: "M", value: "monday" },
  { label: "T", value: "tuesday" },
  { label: "W", value: "wednesday" },
  { label: "T", value: "thursday" },
  { label: "F", value: "friday" },
  { label: "S", value: "saturday" },
  { label: "S", value: "sunday" },
] as const;

type DayOfWeek = (typeof DAY_OPTIONS)[number]["value"];

const choreFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string(),
  expectedDurationMins: z
    .number("Duration must be at least 1 minute")
    .int()
    .positive("Duration must be at least 1 minute")
    .max(24 * 60, "Duration must not be more than 24 hours"),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date"),
  startTime: z.string().regex(/^\d{2}:\d{2}$/, "Invalid time"),
  scheduleFrequency: z.enum(["daily", "weekly", "monthly"]),
  scheduleInterval: z.number("Interval must be at least 1").int().min(1, "Must be at least 1"),
  scheduleDays: z.array(
    z.enum(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]),
  ),
});

type NewChoreDialogProps = {
  choreScheduleId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function NewChoreDialog({ choreScheduleId, open, onOpenChange }: NewChoreDialogProps) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: Parameters<typeof createChore>[1]) => createChore(choreScheduleId, data),
  });

  const form = useForm({
    defaultValues: {
      name: "",
      description: "",
      expectedDurationMins: 30,
      startDate: Temporal.Now.plainDateISO().toString(),
      startTime: "09:00",
      scheduleFrequency: "weekly" as "daily" | "weekly" | "monthly",
      scheduleInterval: 1,
      scheduleDays: [] as DayOfWeek[],
    },
    validators: {
      onBlur: choreFormSchema,
    },
    onSubmit: async ({ value }) => {
      const { scheduleFrequency, scheduleInterval, scheduleDays } = value;
      const intervalPart = scheduleInterval > 1 ? { interval: scheduleInterval } : {};
      let schedule: Schedule;
      if (scheduleFrequency === "weekly") {
        schedule = {
          frequency: "weekly",
          ...intervalPart,
          ...(scheduleDays.length > 0 ? { onDays: scheduleDays } : {}),
        };
      } else if (scheduleFrequency === "daily") {
        schedule = { frequency: "daily", ...intervalPart };
      } else {
        schedule = { frequency: "monthly", ...intervalPart };
      }

      await mutation.mutateAsync({
        name: value.name,
        ...(value.description ? { description: value.description } : {}),
        expectedDurationMins: value.expectedDurationMins,
        startDateTime: Temporal.PlainDate.from(value.startDate)
          .toPlainDateTime(Temporal.PlainTime.from(value.startTime))
          .toString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        schedule,
      });
      await queryClient.invalidateQueries({
        queryKey: ["chores", choreScheduleId],
      });
      onOpenChange(false);
      form.reset();
    },
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) form.reset();
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New Chore</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.Field name="name">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor="chore-name">Name</Label>
                <Input
                  id="chore-name"
                  placeholder="e.g. Vacuum living room"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  autoFocus
                />
                <FieldErrors meta={field.state.meta} />
              </div>
            )}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor="chore-description">Description</Label>
                <Textarea
                  id="chore-description"
                  placeholder="Optional notes"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  rows={2}
                />
                <FieldErrors meta={field.state.meta} />
              </div>
            )}
          </form.Field>

          <form.Field name="expectedDurationMins">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor="chore-duration">Expected duration (minutes)</Label>
                <Input
                  id="chore-duration"
                  type="number"
                  min={1}
                  placeholder="e.g. 30"
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  onBlur={field.handleBlur}
                />
                <FieldErrors meta={field.state.meta} />
              </div>
            )}
          </form.Field>

          <form.Field name="startDate">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor="chore-start-date">Start date</Label>
                <Input
                  id="chore-start-date"
                  type="date"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldErrors meta={field.state.meta} />
              </div>
            )}
          </form.Field>

          <form.Field name="startTime">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor="chore-start-time">Start time</Label>
                <Input
                  id="chore-start-time"
                  type="time"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldErrors meta={field.state.meta} />
              </div>
            )}
          </form.Field>

          <div className="space-y-2">
            <Label>Every</Label>
            <div className="flex gap-2">
              <form.Field name="scheduleInterval">
                {(field) => (
                  <Input
                    type="number"
                    min={1}
                    className="w-16"
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
              <form.Field name="scheduleFrequency">
                {(field) => (
                  <Select
                    value={field.state.value}
                    onValueChange={(v) => field.handleChange(v as "daily" | "weekly" | "monthly")}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">days</SelectItem>
                      <SelectItem value="weekly">weeks</SelectItem>
                      <SelectItem value="monthly">months</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </form.Field>
            </div>
            <form.Field name="scheduleInterval">
              {(field) => <FieldErrors meta={field.state.meta} />}
            </form.Field>
          </div>

          <div className="space-y-1">
            <form.Subscribe selector={(state) => state.values.scheduleFrequency}>
              {(frequency) =>
                frequency === "weekly" ? (
                  <form.Field name="scheduleDays">
                    {(field) => (
                      <div className="space-y-1">
                        <Label>On</Label>
                        <div className="flex items-center gap-1">
                          {DAY_OPTIONS.map((day, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => {
                                const current = field.state.value;
                                const next = current.includes(day.value)
                                  ? current.filter((d) => d !== day.value)
                                  : [...current, day.value];
                                field.handleChange(next);
                              }}
                              className={cn(
                                "flex h-8 w-8 items-center justify-center rounded-full border text-sm font-medium transition-colors",
                                field.state.value.includes(day.value)
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
                              )}
                            >
                              {day.label}
                            </button>
                          ))}
                        </div>
                        <FieldErrors meta={field.state.meta} />
                      </div>
                    )}
                  </form.Field>
                ) : null
              }
            </form.Subscribe>
          </div>

          {mutation.isError && (
            <p className="text-sm text-destructive">Failed to create chore. Please try again.</p>
          )}

          <DialogFooter>
            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.isSubmitting,
                state.isDirty,
                state.values.scheduleFrequency,
                state.values.scheduleDays,
              ]}
            >
              {([canSubmit, isSubmitting, isDirty, scheduleFrequency, scheduleDays]) => (
                <Button
                  type="submit"
                  disabled={
                    !canSubmit ||
                    !!isSubmitting ||
                    !isDirty ||
                    (scheduleFrequency === "weekly" && (scheduleDays as DayOfWeek[]).length === 0)
                  }
                >
                  {isSubmitting ? "Creating…" : "Create"}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
